import React from "react";
import { confirm } from "@app/services/confirm";
import { FormPanelHostProvider, type FormPanelHostValue } from "@shared/ui/form-panel";
import { useFormDirtyTracking } from "@src/components/form";
import { useWindowControls } from "@shared/ui/overlay/hooks/useWindowControls";
import { useOverlayDismissRegistration } from "@shared/ui/overlay/overlay-dismiss-bridge";
import type { MobileFormWindowBehavior } from "./types";

const UNSAVED_CONFIRM_TITLE = "Несохранённые изменения";
const UNSAVED_CONFIRM_MESSAGE = "Есть несохранённые изменения. Закрыть?";

/**
 * Host для form window: dismiss + unsaved guard + close после submit — на уровне definition, не в FormPanel.
 */
export function withMobileFormWindowHost<TProps extends object>(
    Content: React.ComponentType<TProps>,
    behavior: MobileFormWindowBehavior,
): React.ComponentType<TProps> {
    const trackUnsavedFormChanges = behavior.trackUnsavedFormChanges ?? false;
    const closeOnSubmit = behavior.closeOnSubmit ?? true;

    function MobileFormWindowContent(props: TProps) {
        const windowControls = useWindowControls();
        const { setTrackingRoot, hasUnsavedChanges } = useFormDirtyTracking(trackUnsavedFormChanges);

        const onRequestDismiss = React.useCallback(() => {
            if (!trackUnsavedFormChanges || !hasUnsavedChanges) {
                windowControls.close();
                return;
            }

            void confirm
                .show({
                    title: UNSAVED_CONFIRM_TITLE,
                    message: UNSAVED_CONFIRM_MESSAGE,
                    confirmText: "Закрыть",
                    cancelText: "Остаться",
                })
                .then((result) => {
                    if (result) {
                        windowControls.close();
                    }
                });
        }, [hasUnsavedChanges, trackUnsavedFormChanges, windowControls]);

        useOverlayDismissRegistration(onRequestDismiss);

        const wrapOnSubmit = React.useCallback<FormPanelHostValue["wrapOnSubmit"]>(
            (onSubmit) => async (values) => {
                await onSubmit?.(values);
                if (closeOnSubmit) {
                    windowControls.close();
                }
            },
            [closeOnSubmit, windowControls],
        );

        const hostValue = React.useMemo<FormPanelHostValue>(
            () => ({
                onRequestDismiss,
                wrapOnSubmit,
                dirtyTrackingEnabled: trackUnsavedFormChanges,
                setDirtyTrackingRoot: setTrackingRoot,
                hasUnsavedChanges,
            }),
            [hasUnsavedChanges, onRequestDismiss, setTrackingRoot, trackUnsavedFormChanges, wrapOnSubmit],
        );

        return (
            <FormPanelHostProvider value={hostValue}>
                <Content {...props} />
            </FormPanelHostProvider>
        );
    }

    MobileFormWindowContent.displayName = `MobileFormWindow(${Content.displayName ?? Content.name ?? "Content"})`;

    return MobileFormWindowContent;
}
